// src/pages/users/UsersPage.jsx
import React, { useState, useEffect } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import Table from '../../components/common/Table';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import InviteUser from '../../components/forms/InviteUser';
import EditUser from '../../components/forms/EditUser';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { userService } from '../../services/userService';
import { useMedicalCenter } from '../../context/MedicalCenterContext';
import { useAlert } from '../../context/AlertContext';

const UsersPage = () => {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const { medicalCenterId } = useMedicalCenter();
  const { push } = useAlert();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modals
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Confirm dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    if (!medicalCenterId) return;
    loadUsers(medicalCenterId);
  }, [medicalCenterId]);

  const loadUsers = async (mcId) => {
    try {
      setLoading(true);
      setError(null);
      const usersData = await userService.getUsersByMedicalCenter(mcId);
      setUsers(usersData);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteSuccess = (message) => {
    push(message, 'success');
    if (medicalCenterId) loadUsers(medicalCenterId);
  };

  const handleEditSuccess = (message) => {
    push(message, 'success');
    if (medicalCenterId) loadUsers(medicalCenterId);
  };

  const renderUserRow = (user) => {
    return (
      <tr key={user.id} className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'Unknown User'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {user.email || 'N/A'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex flex-wrap gap-1">
            {user.roles && user.roles.length > 0 ? (
              user.roles.map((role, index) => (
                <StatusBadge key={index} status={role.name || role} type="role" />
              ))
            ) : (
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                No Role
              </span>
            )}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
          <div className="flex space-x-2">
            {canEdit('users') && (
              <Button size="sm" variant="outline" onClick={() => handleEditUser(user)}>
                Edit
              </Button>
            )}
            {canDelete('users') && (
              <Button size="sm" variant="danger" onClick={() => handleDeleteUser(user)}>
                Delete
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    if (!canDelete('users')) {
      push('You do not have permission to delete users.', 'warning');
      return;
    }
    setPendingDelete(user);
    setShowDeleteDialog(true);
  };

  const confirmDeleteUser = async () => {
    try {
      await userService.deleteUser(pendingDelete.id);
      if (medicalCenterId) await loadUsers(medicalCenterId);
      push('User deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting user:', err);
      push('Failed to delete user: ' + err.message, 'error');
    } finally {
      setShowDeleteDialog(false);
      setPendingDelete(null);
    }
  };

  if (!medicalCenterId) {
    return <div className="p-6 text-gray-600">Select a medical center to continue.</div>;
  }

  if (loading) return <LoadingSpinner />;
  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={() => loadUsers(medicalCenterId)}
        title="Error Loading Users"
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        subtitle="Manage system users and their roles"
        actionText="Invite User"
        onAction={() => setIsInviteModalOpen(true)}
        showAction={canCreate('users')}
      />

      <div className="bg-white shadow rounded-lg">
        <Table
          headers={['Name', 'Email', 'Roles', 'Actions']}
          data={users}
          renderRow={renderUserRow}
          loading={loading}
        />
      </div>

      <InviteUser
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />

      <EditUser
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={handleEditSuccess}
        user={selectedUser}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setPendingDelete(null);
        }}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete "${pendingDelete?.first_name || ''} ${pendingDelete?.last_name || ''}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default UsersPage;